import { Alert, Button, Card, DatePicker, Descriptions, Form, Input, Select, Space, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Action = {
  id: string;
  action_text: string;
  due_on?: string | null;
  status: string;
  completed_on?: string | null;
};

type Incident = {
  id: string;
  incident_no: string;
  incident_on: string;
  category: string;
  severity: string;
  summary: string;
  detail_text?: string | null;
  status: string;
  record_version: number;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  section_name?: string;
  actions: Action[];
};

export default function BehaviourDetailPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.behaviour.manage') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-behaviour-incident', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Incident>(`/education/behaviour/incidents/${id}`)).data,
  });
  const incident = query.data;
  const open = incident && !['closed', 'cancelled'].includes(incident.status);

  const setStatus = useMutation({
    mutationFn: (status: string) =>
      api.post(`/education/behaviour/incidents/${id}/status`, {
        status,
        record_version: incident?.record_version,
      }),
    onSuccess: () => {
      message.success(t('behaviourStatusUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-behaviour-incident', id] });
      queryClient.invalidateQueries({ queryKey: ['education-behaviour'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const addAction = useMutation({
    mutationFn: (values: { action_text: string; due_on?: dayjs.Dayjs }) =>
      api.post(`/education/behaviour/incidents/${id}/actions`, {
        action_text: values.action_text,
        due_on: values.due_on ? values.due_on.format('YYYY-MM-DD') : undefined,
      }),
    onSuccess: () => {
      message.success(t('behaviourActionAdded'));
      queryClient.invalidateQueries({ queryKey: ['education-behaviour-incident', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const actionStatus = useMutation({
    mutationFn: ({ actionId, status }: { actionId: string; status: string }) =>
      api.post(`/education/behaviour/actions/${actionId}/status`, { status }),
    onSuccess: () => {
      message.success(t('behaviourActionUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-behaviour-incident', id] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={incident?.incident_no || t('behaviour')}
      description={t('behaviourIntro')}
      tags={incident ? <Tag>{incident.status}</Tag> : null}
      extra={<Button onClick={() => navigate('/education/behaviour')}>{t('behaviour')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('behaviourNonSafeguardingHint')} />
        <Card size="small" loading={query.isLoading}>
          {incident ? (
            <Descriptions column={{ xs: 1, md: 2 }} size="small">
              <Descriptions.Item label={t('student')}>
                {`${incident.admission_no || ''} — ${incident.first_name || ''} ${incident.last_name || ''}`.trim()}
              </Descriptions.Item>
              <Descriptions.Item label={t('section')}>{incident.section_name || '—'}</Descriptions.Item>
              <Descriptions.Item label={t('incidentOn')}>{incident.incident_on}</Descriptions.Item>
              <Descriptions.Item label={t('category')}>{incident.category}</Descriptions.Item>
              <Descriptions.Item label={t('severity')}>
                <Tag color={incident.severity === 'high' ? 'red' : incident.severity === 'medium' ? 'orange' : undefined}>
                  {incident.severity}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('summary')} span={2}>
                {incident.summary}
              </Descriptions.Item>
              <Descriptions.Item label={t('detail')} span={2}>
                {incident.detail_text || '—'}
              </Descriptions.Item>
            </Descriptions>
          ) : null}
          {canManage && open ? (
            <Space wrap style={{ marginTop: 16 }}>
              <Button onClick={() => setStatus.mutate('in_progress')} loading={setStatus.isPending}>
                {t('statusInProgress')}
              </Button>
              <Button type="primary" onClick={() => setStatus.mutate('closed')} loading={setStatus.isPending}>
                {t('statusClosed')}
              </Button>
              <Button danger onClick={() => setStatus.mutate('cancelled')} loading={setStatus.isPending}>
                {t('statusCancelled')}
              </Button>
            </Space>
          ) : null}
        </Card>

        <Card size="small" title={t('behaviourActions')}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={incident?.actions || []}
            columns={[
              { title: t('action'), dataIndex: 'action_text' },
              { title: t('dueOn'), dataIndex: 'due_on', width: 120, render: (v) => v || '—' },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 100,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('actions'),
                width: 180,
                render: (_: unknown, row: Action) =>
                  row.status === 'open' && canManage && open ? (
                    <Space size={4}>
                      <Button size="small" onClick={() => actionStatus.mutate({ actionId: row.id, status: 'done' })}>
                        {t('done')}
                      </Button>
                      <Button size="small" onClick={() => actionStatus.mutate({ actionId: row.id, status: 'cancelled' })}>
                        {t('cancel')}
                      </Button>
                    </Space>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
          {canManage && open ? (
            <Form
              layout="vertical"
              style={{ maxWidth: 520, marginTop: 16 }}
              onFinish={(values) => addAction.mutate(values)}
            >
              <Form.Item name="action_text" label={t('action')} rules={[{ required: true }]}>
                <Input.TextArea rows={2} maxLength={500} />
              </Form.Item>
              <Form.Item name="due_on" label={t('dueOn')}>
                <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={addAction.isPending}>
                {t('addAction')}
              </Button>
            </Form>
          ) : null}
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
